import { track } from './amplitude';
import { AnalyticsEvent } from './events';

/**
 * Captures UTM parameters from email links and fires an Amplitude event.
 *
 * When a user clicks a link in an email (e.g., manager digest), the URL contains
 * UTM params like:
 *   ?utm_source=bonus.ly&utm_medium=email&utm_campaign=manager_digest_recognize_team
 *
 * This hook:
 * 1. Reads UTM params from the current URL
 * 2. If utm_medium is "email", fires an "Email Link Clicked" event to Amplitude
 * 3. Cleans UTM params from the URL to prevent re-tracking on navigation
 *
 * Must be rendered after AuthProvider/Amplitude initialization so tracking is active.
 */

// Module-level flag ensures tracking fires once per page load, even across component remounts.
let hasTracked = false;

export const useEmailAttribution = () => {
	if (hasTracked) {
		return;
	}

	const searchParams = new URLSearchParams(window.location.search);
	const utmMedium = searchParams.get('utm_medium');

	// Only track email-originated visits
	if (utmMedium !== 'email') {
		return;
	}

	hasTracked = true;

	track(AnalyticsEvent.Email.LinkVisited, {
		utm_source: searchParams.get('utm_source'),
		utm_medium: utmMedium,
		utm_campaign: searchParams.get('utm_campaign'),
		landing_path: window.location.pathname,
	});

	// Clean UTM params from URL to avoid re-tracking and keep the URL tidy.
	// Preserve any non-UTM params (e.g., give_recognition=true).
	searchParams.delete('utm_source');
	searchParams.delete('utm_medium');
	searchParams.delete('utm_campaign');
	searchParams.delete('utm_content');
	searchParams.delete('utm_term');

	const cleanSearch = searchParams.toString();
	const cleanUrl = `${window.location.pathname}${cleanSearch ? `?${cleanSearch}` : ''}${window.location.hash}`;

	window.history.replaceState(window.history.state, '', cleanUrl);
};
